// src/features/legal/LegalPage.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Menu, Segment } from 'semantic-ui-react';

const LegalPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState<'privacy' | 'terms'>('privacy');

  const handleItemClick = (name: 'privacy' | 'terms') => {
    setActiveItem(name);
  };

  return (
    <Container>
      <Menu pointing secondary>
        <Menu.Item
          name={t('legal.navigation.privacyPolicy')}
          active={activeItem === 'privacy'}
          onClick={() => handleItemClick('privacy')}
        />
        <Menu.Item
          name={t('legal.navigation.termsOfService')}
          active={activeItem === 'terms'}
          onClick={() => handleItemClick('terms')}
        />
      </Menu>

      <Segment>
        {activeItem === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}
      </Segment>
    </Container>
  );
};

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <h1>{t('legal.privacy.title')}</h1>
      
      <div style={{ background: '#f8f9fa', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>
        <strong>{t('legal.privacy.lastUpdated')}</strong> {t('legal.privacy.lastUpdatedDate')}
      </div>

      <p>{t('legal.privacy.intro')}</p>

      <h2>{t('legal.privacy.sections.informationWeCollect.title')}</h2>
      <h3>{t('legal.privacy.sections.informationWeCollect.informationYouProvide.title')}</h3>
      <ul>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.informationYouProvide.accountInfo')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.informationYouProvide.profileInfo')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.informationYouProvide.eventData')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.informationYouProvide.communications')}</strong></li>
      </ul>

      <h3>{t('legal.privacy.sections.informationWeCollect.automaticallyCollected.title')}</h3>
      <ul>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.automaticallyCollected.usageData')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.automaticallyCollected.deviceInfo')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationWeCollect.automaticallyCollected.logData')}</strong></li>
      </ul>

      <h2>{t('legal.privacy.sections.howWeUse.title')}</h2>
      <p>{t('legal.privacy.sections.howWeUse.intro')}</p>
      <ul>
        {(t('legal.privacy.sections.howWeUse.purposes', { returnObjects: true }) as string[]).map((purpose: string, index: number) => (
          <li key={index}>{purpose}</li>
        ))}
      </ul>

      <h2>{t('legal.privacy.sections.informationSharing.title')}</h2>
      <p>{t('legal.privacy.sections.informationSharing.intro')}</p>
      <ul>
        <li><strong>{t('legal.privacy.sections.informationSharing.eventOrganizers')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationSharing.serviceProviders')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationSharing.legalRequirements')}</strong></li>
        <li><strong>{t('legal.privacy.sections.informationSharing.businessTransfers')}</strong></li>
      </ul>

      <h2>{t('legal.privacy.sections.dataStorage.title')}</h2>
      <p>{t('legal.privacy.sections.dataStorage.content')}</p>

      <h2>{t('legal.privacy.sections.userRights.title')}</h2>
      <p>{t('legal.privacy.sections.userRights.intro')}</p>
      <ul>
        <li><strong>{t('legal.privacy.sections.userRights.access')}</strong></li>
        <li><strong>{t('legal.privacy.sections.userRights.update')}</strong></li>
        <li><strong>{t('legal.privacy.sections.userRights.delete')}</strong></li>
        <li><strong>{t('legal.privacy.sections.userRights.portability')}</strong></li>
        <li><strong>{t('legal.privacy.sections.userRights.withdrawConsent')}</strong></li>
      </ul>

      <h2>{t('legal.privacy.sections.childrenPrivacy.title')}</h2>
      <p>{t('legal.privacy.sections.childrenPrivacy.content')}</p>

      <h2>{t('legal.privacy.sections.internationalTransfers.title')}</h2>
      <p>{t('legal.privacy.sections.internationalTransfers.content')}</p>

      <h2>{t('legal.privacy.sections.policyChanges.title')}</h2>
      <p>{t('legal.privacy.sections.policyChanges.content')}</p>

      <h2>{t('legal.privacy.sections.thirdPartyServices.title')}</h2>
      <p>{t('legal.privacy.sections.thirdPartyServices.content')}</p>

      <div style={{ background: '#e8f5e8', padding: '15px', borderRadius: '5px', marginTop: '20px' }}>
        <h2>{t('legal.privacy.sections.contact.title')}</h2>
        <p>{t('legal.privacy.sections.contact.intro')}</p>
        <p>
          <strong>{t('legal.privacy.sections.contact.email')}</strong> {t('legal.privacy.sections.contact.emailValue')}<br />
          <strong>{t('legal.privacy.sections.contact.address')}</strong> {t('legal.privacy.sections.contact.addressValue')}<br />
          <strong>{t('legal.privacy.sections.contact.phone')}</strong> {t('legal.privacy.sections.contact.phoneValue')}
        </p>
      </div>
    </div>
  );
};

const TermsOfService: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <h1>{t('legal.terms.title')}</h1>
      
      <div style={{ background: '#f8f9fa', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>
        <strong>{t('legal.terms.lastUpdated')}</strong> {t('legal.terms.lastUpdatedDate')}
      </div>

      <p>{t('legal.terms.intro')}</p>

      <h2>{t('legal.terms.sections.acceptance.title')}</h2>
      <p>{t('legal.terms.sections.acceptance.content')}</p>

      <h2>{t('legal.terms.sections.description.title')}</h2>
      <p>{t('legal.terms.sections.description.content')}</p>

      <h2>{t('legal.terms.sections.userAccounts.title')}</h2>
      <p>{t('legal.terms.sections.userAccounts.intro')}</p>
      <ul>
        {(t('legal.terms.sections.userAccounts.responsibilities', { returnObjects: true }) as string[]).map((responsibility: string, index: number) => (
          <li key={index}>{responsibility}</li>
        ))}
      </ul>

      <h2>{t('legal.terms.sections.acceptableUse.title')}</h2>
      <p>{t('legal.terms.sections.acceptableUse.intro')}</p>
      <ul>
        {(t('legal.terms.sections.acceptableUse.restrictions', { returnObjects: true }) as string[]).map((restriction: string, index: number) => (
          <li key={index}>{restriction}</li>
        ))}
      </ul>

      <h2>{t('legal.terms.sections.userContent.title')}</h2>
      <p>{t('legal.terms.sections.userContent.content')}</p>

      <h2>{t('legal.terms.sections.eventParticipation.title')}</h2>
      <p>{t('legal.terms.sections.eventParticipation.intro')}</p>
      <ul>
        {(t('legal.terms.sections.eventParticipation.rules', { returnObjects: true }) as string[]).map((rule: string, index: number) => (
          <li key={index}>{rule}</li>
        ))}
      </ul>

      <div style={{ background: '#fff3cd', padding: '15px', borderLeft: '4px solid #ffc107', margin: '20px 0' }}>
        <h2>{t('legal.terms.sections.liability.title')}</h2>
        <p><strong>{t('legal.terms.sections.liability.important')}</strong> {t('legal.terms.sections.liability.disclaimer')}</p>
        
        <p>{t('legal.terms.sections.liability.limitation')}</p>
        
        <p>{t('legal.terms.sections.liability.totalLiability')}</p>
      </div>

      <h2>{t('legal.terms.sections.indemnification.title')}</h2>
      <p>{t('legal.terms.sections.indemnification.content')}</p>

      <h2>{t('legal.terms.sections.privacy.title')}</h2>
      <p>{t('legal.terms.sections.privacy.content')}</p>

      <h2>{t('legal.terms.sections.intellectualProperty.title')}</h2>
      <p>{t('legal.terms.sections.intellectualProperty.content')}</p>

      <h2>{t('legal.terms.sections.termination.title')}</h2>
      <p>{t('legal.terms.sections.termination.content')}</p>

      <h2>{t('legal.terms.sections.termsChanges.title')}</h2>
      <p>{t('legal.terms.sections.termsChanges.content')}</p>

      <h2>{t('legal.terms.sections.governingLaw.title')}</h2>
      <p>{t('legal.terms.sections.governingLaw.content')}</p>

      <h2>{t('legal.terms.sections.disputeResolution.title')}</h2>
      <p>{t('legal.terms.sections.disputeResolution.content')}</p>

      <h2>{t('legal.terms.sections.severability.title')}</h2>
      <p>{t('legal.terms.sections.severability.content')}</p>

      <h2>{t('legal.terms.sections.entireAgreement.title')}</h2>
      <p>{t('legal.terms.sections.entireAgreement.content')}</p>

      <div style={{ background: '#e8f5e8', padding: '15px', borderRadius: '5px', marginTop: '20px' }}>
        <h2>{t('legal.terms.sections.contact.title')}</h2>
        <p>{t('legal.terms.sections.contact.intro')}</p>
        <p>
          <strong>{t('legal.terms.sections.contact.email')}</strong> {t('legal.terms.sections.contact.emailValue')}<br />
          <strong>{t('legal.terms.sections.contact.address')}</strong> {t('legal.terms.sections.contact.addressValue')}<br />
          <strong>{t('legal.terms.sections.contact.phone')}</strong> {t('legal.terms.sections.contact.phoneValue')}
        </p>
      </div>
    </div>
  );
};

export default LegalPage;